import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/EditOutlined";
import { QueryState } from "@/components/common/AsyncState";
import { createRoom, fetchBranches, fetchRooms, updateRoom } from "@/api/branches";
import type { Room } from "@/types/branch";

function RoomDialog({ open, onClose, room }: { open: boolean; onClose: () => void; room?: Room }) {
  const queryClient = useQueryClient();
  const { data: branches } = useQuery({ queryKey: ["branches"], queryFn: fetchBranches });
  const [branchId, setBranchId] = useState(room?.branchId ?? "");
  const [name, setName] = useState(room?.name ?? "");
  const [capacity, setCapacity] = useState(room?.capacity ?? 10);

  const mutation = useMutation({
    mutationFn: () => {
      const payload = { branchId, name, capacity };
      return room ? updateRoom(room.id, payload) : createRoom(payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["rooms"] });
      onClose();
    },
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{room ? "Редактирование кабинета" : "Новый кабинет"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField select label="Филиал" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
            {(branches ?? []).map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField label="Название кабинета" value={name} onChange={(e) => setName(e.target.value)} />
          <TextField label="Вместимость" type="number" value={capacity} onChange={(e) => setCapacity(Number(e.target.value))} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Отмена
        </Button>
        <Button variant="contained" disabled={!branchId || !name || mutation.isPending} onClick={() => mutation.mutate()}>
          Сохранить
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function RoomsTab() {
  const { data: branches } = useQuery({ queryKey: ["branches"], queryFn: fetchBranches });
  const { data, isLoading, error } = useQuery({ queryKey: ["rooms"], queryFn: () => fetchRooms() });
  const [dialogState, setDialogState] = useState<{ open: boolean; room?: Room }>({ open: false });

  function branchName(branchId: string) {
    return branches?.find((b) => b.id === branchId)?.name ?? branchId;
  }

  return (
    <>
      <Stack direction="row" justifyContent="flex-end" mb={2}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogState({ open: true })}>
          Новый кабинет
        </Button>
      </Stack>
      <QueryState isLoading={isLoading} error={error}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Кабинет</TableCell>
              <TableCell>Филиал</TableCell>
              <TableCell>Вместимость</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {(data ?? []).map((room) => (
              <TableRow key={room.id}>
                <TableCell>{room.name}</TableCell>
                <TableCell>{branchName(room.branchId)}</TableCell>
                <TableCell>{room.capacity}</TableCell>
                <TableCell align="right">
                  <Button size="small" startIcon={<EditIcon />} onClick={() => setDialogState({ open: true, room })}>
                    Изменить
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </QueryState>
      <RoomDialog open={dialogState.open} room={dialogState.room} onClose={() => setDialogState({ open: false })} />
    </>
  );
}
